/**
 * AddBidModal — create or edit a Bid (Supabase) with leveling deltas, CSI division
 * breakdown, and line items.
 *
 * Opens via CustomEvent('open-add-bid-modal', { detail }):
 *   create: { campaignId, campaignStatus }
 *   edit:   { campaignId, bidId }
 *
 * On create, also runs setLineItems and auto-advances the campaign preview→open.
 * On success busts ctx.bidCampaignDetailCache[campaignId] + ctx.bidCampaignCache.
 */

import { useState, useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import {
  createBid, updateBid, setLineItems, getBidDetail, updateCampaign, CONTRACTING_METHODS,
} from '../services/bidCampaigns.js'
import { getOrganizations } from '../services/design.js'
import Select from './Select.jsx'

const DELTA_FIELDS = [
  ['allowance_baseline_delta', 'Allowance'],
  ['alternate_selection_delta', 'Alternates'],
  ['excluded_scope_delta', 'Excluded scope'],
  ['bidder_included_extra_deduct', 'Bidder extra (deduct)'],
  ['tax_permits_delta', 'Tax + permits'],
  ['gc_bonding_delta', 'GC bonding'],
]
const DIVISION_FIELDS = [
  ['division_01', 'Div 01'], ['division_02', 'Div 02'], ['division_03', 'Div 03'],
  ['division_04', 'Div 04'], ['division_05', 'Div 05'],
]

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const numOrNull = v => (v === '' || v == null ? null : Number(v))

export default function AddBidModal() {
  const [open,       setOpen]       = useState(false)
  const [campaignId, setCampaignId] = useState(null)
  const [campStatus, setCampStatus] = useState(null)
  const [bidId,      setBidId]      = useState(null)
  const [orgId,      setOrgId]      = useState('')
  const [orgName,    setOrgName]    = useState('')
  const [orgs,       setOrgs]       = useState(null)
  const [dateRecv,   setDateRecv]   = useState('')
  const [priceRaw,   setPriceRaw]   = useState('')
  const [method,     setMethod]     = useState('')
  const [deltas,     setDeltas]     = useState({})
  const [divs,       setDivs]       = useState({})
  const [items,      setItems]      = useState([])   // [{ name, sectionCode, amount }]
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)
  const orgsLoaded = useRef(false)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setCampaignId(d.campaignId || null)
      setCampStatus(d.campaignStatus || null)
      setBidId(d.bidId || null)
      setOrgId(''); setOrgName(''); setDateRecv(''); setPriceRaw(''); setMethod('')
      setDeltas({}); setDivs({}); setItems([])
      setError(null); setSubmitting(false); setOpen(true)

      if (ctx.orgsCache && ctx.orgsCache.length > 0) setOrgs(ctx.orgsCache)
      else if (!orgsLoaded.current) {
        orgsLoaded.current = true
        getOrganizations().then(list => { ctx.orgsCache = list; setOrgs(list) }).catch(() => setOrgs([]))
      }

      // Edit: load existing bid + line items
      if (d.bidId) {
        getBidDetail(d.bidId).then(({ bid, lineItems }) => {
          if (!bid) return
          setOrgId(bid.bidder_airtable_id || '')
          setOrgName(bid.bidder_name || '')
          setDateRecv(bid.date_received || '')
          setPriceRaw(bid.price_raw != null ? String(bid.price_raw) : '')
          setMethod(bid.contracting_method || '')
          const dl = {}; DELTA_FIELDS.forEach(([k]) => { if (bid[k] != null) dl[k] = String(bid[k]) }); setDeltas(dl)
          const dv = {}; DIVISION_FIELDS.forEach(([k]) => { if (bid[k] != null) dv[k] = String(bid[k]) }); setDivs(dv)
          setItems((lineItems || []).map(li => ({ name: li.name || '', sectionCode: li.section_code || '', amount: li.amount != null ? String(li.amount) : '' })))
        }).catch(() => {})
      }
    }
    window.addEventListener('open-add-bid-modal', onOpen)
    return () => window.removeEventListener('open-add-bid-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function pickOrg(id) {
    setOrgId(id)
    const o = (orgs || []).find(x => x.id === id)
    setOrgName(o ? o.name : '')
  }

  async function handleSubmit() {
    if (!orgId) return
    setSubmitting(true); setError(null)
    const fields = {
      bidder_airtable_id: orgId,
      bidder_name: orgName || null,
      date_received: dateRecv || null,
      price_raw: numOrNull(priceRaw),
      contracting_method: method || null,
    }
    DELTA_FIELDS.forEach(([k]) => { fields[k] = numOrNull(deltas[k]) || 0 })
    DIVISION_FIELDS.forEach(([k]) => { fields[k] = numOrNull(divs[k]) })
    const liRows = items
      .filter(it => (it.name && it.name.trim()) || it.amount)
      .map(it => ({ name: it.name || null, sectionCode: it.sectionCode || null, amount: numOrNull(it.amount) }))
    try {
      let targetBidId = bidId
      if (bidId) {
        await updateBid(bidId, fields)
      } else {
        const bid = await createBid(campaignId, fields)
        targetBidId = bid && bid.id
        if (campStatus === 'preview') await updateCampaign(campaignId, { status: 'open' })
      }
      if (targetBidId) await setLineItems(targetBidId, liRows)
      if (ctx.bidCampaignDetailCache) ctx.bidCampaignDetailCache[campaignId] = undefined
      ctx.bidCampaignCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save bid'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = orgId && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddBidModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 520, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {bidId ? 'Edit Bid' : 'Add Bid'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Bidder <span style={{ color: '#c0392b' }}>*</span></label>
              {orgs === null ? (
                <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' }}>Loading…</div>
              ) : (
                <Select value={orgId} onChange={pickOrg} options={(orgs || []).map(o => ({ value: o.id, label: o.name }))}
                  placeholder="Select bidder…" searchable style={{ width: '100%', fontSize: 12 }} />
              )}
            </div>
            <div style={{ width: 150 }}>
              <label style={labelStyle}>Date Received</label>
              <input type="date" style={inputStyle} value={dateRecv} onChange={e => setDateRecv(e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Bid Price (raw)</label>
              <input type="number" style={inputStyle} value={priceRaw} onChange={e => setPriceRaw(e.target.value)} placeholder="0" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Contracting Method</label>
              <Select value={method} onChange={setMethod}
                options={[{ value: '', label: '— None —' }].concat(CONTRACTING_METHODS.map(m => ({ value: m, label: m })))}
                placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Leveling adjustments (added to raw → normalized)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {DELTA_FIELDS.map(([k, lbl]) => (
                <div key={k}>
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 9, color: 'var(--aj-text-muted)', marginBottom: 2 }}>{lbl}</div>
                  <input type="number" style={inputStyle} value={deltas[k] || ''} onChange={e => setDeltas({ ...deltas, [k]: e.target.value })} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>CSI division breakdown (optional)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 6 }}>
              {DIVISION_FIELDS.map(([k, lbl]) => (
                <div key={k}>
                  <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 9, color: 'var(--aj-text-muted)', marginBottom: 2 }}>{lbl}</div>
                  <input type="number" style={inputStyle} value={divs[k] || ''} onChange={e => setDivs({ ...divs, [k]: e.target.value })} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Line items (optional)</label>
              <button onClick={() => setItems([...items, { name: '', sectionCode: '', amount: '' }])}
                style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer' }}>+ line item</button>
            </div>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                <input style={{ ...inputStyle, flex: 1 }} value={it.name} onChange={e => { const c = [...items]; c[i] = { ...it, name: e.target.value }; setItems(c) }} placeholder="Description" />
                <input type="number" style={{ ...inputStyle, width: 110 }} value={it.amount} onChange={e => { const c = [...items]; c[i] = { ...it, amount: e.target.value }; setItems(c) }} placeholder="Amount" />
                <button onClick={() => setItems(items.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, padding: '0 4px' }}>×</button>
              </div>
            ))}
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-mid)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (bidId ? 'Save' : 'Add Bid')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
